import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { Add, Delete, Refresh } from '@mui/icons-material';

import {
  deleteCampaignDetail,
  getCampaignDetails,
} from '../../../api/campaign';

import RecipientPickerDialog from './RecipientPickerDialog';

const formatDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('hu-HU');
};

const getMailStatusLabel = (status) => {
  switch (Number(status)) {
    case 10:
      return 'Várakozik';
    case 20:
      return 'Küldés alatt';
    case 30:
      return 'Elküldve';
    case 40:
      return 'Hiba';
    default:
      return status ?? 'Nincs státusz';
  }
};

const getMailStatusColor = (status) => {
  switch (Number(status)) {
    case 10:
      return 'default';
    case 20:
      return 'warning';
    case 30:
      return 'success';
    case 40:
      return 'error';
    default:
      return 'default';
  }
};

const FormMailCampaignDetails = ({ campaignId }) => {
  const [details, setDetails] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  async function loadDetails() {
    if (!campaignId) return;

    setLoading(true);

    try {
      const result = await getCampaignDetails(campaignId);
      setDetails(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Kampány címzettek betöltési hiba:', error);
      setDetails([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDetails();
  }, [campaignId]);

  const handleDelete = async (detail) => {
    const confirmed = window.confirm(
      `Biztosan törlöd ezt a címzettet?\n\n${detail.user?.text ?? ''}`,
    );

    if (!confirmed) return;

    await deleteCampaignDetail(detail.id);
    await loadDetails();
  };

  return (
    <Card variant='outlined' sx={{ borderRadius: 3 }}>
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent='space-between'
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant='subtitle1' fontWeight={700}>
              Kampány címzettek
            </Typography>

            <Typography variant='body2' color='text.secondary'>
              Hozzárendelt felhasználók és e-mail küldési állapotok
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant='outlined'
              startIcon={<Refresh />}
              onClick={loadDetails}
              disabled={loading}
            >
              Frissítés
            </Button>

            <Button
              variant='contained'
              startIcon={<Add />}
              onClick={() => setPickerOpen(true)}
            >
              Címzettek hozzáadása
            </Button>
          </Stack>
        </Stack>

        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Felhasználó</TableCell>
              <TableCell>Küldés állapota</TableCell>
              <TableCell>Üzenet elküldve</TableCell>
              <TableCell align='right'>Műveletek</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {details.map((detail) => (
              <TableRow key={detail.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>
                    {detail.user?.text ?? detail.userText ?? '-'}
                  </Typography>

                  {(detail.user?.email || detail.email) && (
                    <Typography variant='body2' color='text.secondary'>
                      {detail.user?.email ?? detail.email}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Chip
                    size='small'
                    label={getMailStatusLabel(detail.mailStatus)}
                    color={getMailStatusColor(detail.mailStatus)}
                    variant='outlined'
                  />
                </TableCell>

                <TableCell>{formatDate(detail.mailSentAt)}</TableCell>

                <TableCell align='right'>
                  <IconButton
                    color='error'
                    onClick={() => handleDelete(detail)}
                    disabled={Number(detail.mailStatus) === 30}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {!loading && details.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color='text.secondary'>
                    Még nincs címzett ehhez a kampányhoz.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {loading && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color='text.secondary'>Betöltés...</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <RecipientPickerDialog
        open={pickerOpen}
        campaignId={campaignId}
        onClose={() => setPickerOpen(false)}
        onInserted={async () => {
          setPickerOpen(false);
          await loadDetails();
        }}
      />
    </Card>
  );
};

export default FormMailCampaignDetails;
